/**
 * GearTestOrchestrator — manages the staged gear-comparison flow.
 *
 * State machine:
 *   READY → BASELINE_MULTI_FLIGHT → BASELINE_DONE → COMPARING → RESULTS
 *     ↑                                                            |
 *     ├──────────────── reset() ───────────────────────────────────┘
 *     │                                                            |
 *     │                                               [TEST GEAR]  |
 *     │                                                    ↓       |
 *     │                                        GEAR_TESTING → GEAR_RESULTS
 *     │                                                            |
 *     │                                            [VIEW RESULTS]  |
 *     │                                                    ↓       |
 *     └──────────────────────────────────────────────── SUMMARY
 *
 * In BASELINE_MULTI_FLIGHT phase, 10 ComparisonShot instances fire with variance.
 * In COMPARING phase, 25 spec configs run in mobile-safe tranches.
 * In GEAR_TESTING phase, 10 gear configs run in mobile-safe tranches.
 * In BALL_TESTING phase, up to 14 ball models run in mobile-safe tranches.
 *
 * Does NOT touch the DOM — emits callbacks for UIManager to handle.
 */

import type { Scene } from '@babylonjs/core/scene';
import { distanceToSegment, type ScreenPoint } from '../camera/FlightPicking';
import type { CascadedShadowGenerator } from '@babylonjs/core/Lights/Shadows/cascadedShadowGenerator';
import type { ClubPreset, EnvironmentConfig, SwingParams, Vec3 } from '../types';
import type { GearSpecs, EquipmentBag, EldrickPayload } from '../equipment/types';
import { unwrapValue } from '../equipment/types';
import type { ShotTracer } from '../rendering/ShotTracer';
import { computeSpecModifiers, computeModifiers, applyModifiers } from '../equipment/EquipmentEngine';
import { createSwingParams } from '../simulation/SwingInput';
import { importedLaunch, comparisonDelivery, importedSpecDetails } from './ImportedFitting';
import { generateSpreadSpecSet, generateNeighborhoodSpecs } from './specRandomizer';
import { generateRandomGearBags, buildGearLabel } from './gearRandomizer';
import { GOLF_BALLS, DRIVERS, IRON_SETS, SHAFTS } from '../equipment/library';
import type { GolfBall } from '../equipment/types';
import { generateVariantParams } from '../simulation/SwingVariance';
import { averageBaselineDelivery } from './ComparisonReference';
import { PatternRunner } from './PatternRunner';
import { pairedEquipmentSwings, seededPatternRandom, quantile } from './ShotPattern';
import type { ShotPattern, PatternSource } from './ShotPattern';
import { ComparisonReview } from '../rendering/ComparisonReview';
import { ComparisonShot } from './ComparisonShot';
import type { ComparisonColor } from './ComparisonShot';
import { DEFAULT_ENVIRONMENT, M_TO_YARDS } from '../constants';

// ── Types ────────────────────────────────────────────────────────────────────

export enum TestPhase {
  Ready = 'ready',
  BaselineMultiFlight = 'baseline_multi_flight',
  BaselineFlight = 'baseline_flight',
  BaselineDone = 'baseline_done',
  Comparing = 'comparing',
  Results = 'results',
  GearTesting = 'gear_testing',
  GearResults = 'gear_results',
  BallTesting = 'ball_testing',
  BallResults = 'ball_results',
  Summary = 'summary',
}

export interface ComparisonEntry {
  pattern?: ShotPattern;
  recommendationSource?: 'core';
  /** Phase-local flight identity; finish order and display rank are not identities. */
  configIndex?: number;
  label: string;
  specs: GearSpecs | null;     // null for baseline
  color: { r: number; g: number; b: number };
  colorHex: string;
  formatted: Record<string, string>;
  specSummary: string;
  spinRate: number;            // actual spin rate used for this shot
  /** Middle-80% full widths in yards; never ± confidence/error bars. */
  spread?: { carrySpread: number; offlineSpread: number };
}

/** 25-color palette for comparison tracers (specs). */
const SPEC_COLORS: ComparisonColor[] = [
  { r: 0.29, g: 0.87, b: 0.50, hex: '#4ade80' },   // green
  { r: 0.38, g: 0.56, b: 1.0,  hex: '#618fff' },    // blue
  { r: 1.0,  g: 0.65, b: 0.22, hex: '#ffa738' },    // orange
  { r: 0.94, g: 0.33, b: 0.31, hex: '#f05450' },    // red
  { r: 0.68, g: 0.45, b: 0.98, hex: '#ad73fa' },    // purple
  { r: 0.98, g: 0.84, b: 0.22, hex: '#fad638' },    // yellow
  { r: 0.22, g: 0.78, b: 0.82, hex: '#38c7d1' },    // teal
  { r: 0.96, g: 0.46, b: 0.68, hex: '#f575ae' },    // pink
  { r: 0.50, g: 0.82, b: 0.33, hex: '#80d154' },    // lime
  { r: 0.70, g: 0.70, b: 0.70, hex: '#b3b3b3' },    // silver
  { r: 1.0,  g: 0.45, b: 0.0,  hex: '#ff7300' },    // deep orange
  { r: 0.22, g: 0.60, b: 1.0,  hex: '#3899ff' },    // sky blue
  { r: 0.85, g: 0.29, b: 0.72, hex: '#d94ab8' },    // magenta
  { r: 0.60, g: 0.90, b: 0.20, hex: '#99e633' },    // chartreuse
  { r: 0.40, g: 0.30, b: 0.90, hex: '#664de6' },    // indigo
  { r: 1.0,  g: 0.78, b: 0.45, hex: '#ffc773' },    // peach
  { r: 0.20, g: 0.85, b: 0.65, hex: '#33d9a6' },    // mint
  { r: 0.90, g: 0.55, b: 0.30, hex: '#e68c4d' },    // copper
  { r: 0.55, g: 0.38, b: 0.80, hex: '#8c61cc' },    // lavender
  { r: 0.82, g: 0.90, b: 0.20, hex: '#d1e633' },    // yellow-green
  { r: 0.30, g: 0.70, b: 0.90, hex: '#4db3e6' },    // cerulean
  { r: 0.95, g: 0.35, b: 0.50, hex: '#f25980' },    // coral
  { r: 0.45, g: 0.75, b: 0.50, hex: '#73bf80' },    // sage
  { r: 0.75, g: 0.60, b: 0.40, hex: '#bf9966' },    // tan
  { r: 0.60, g: 0.20, b: 0.60, hex: '#993399' },    // plum
];

/** 10-color subset for gear testing. */
const GEAR_COLORS: ComparisonColor[] = SPEC_COLORS.slice(0, 10);

/** Color palette for ball testing (up to 14 balls). */
const BALL_COLORS: ComparisonColor[] = [
  { r: 1.0,  g: 0.84, b: 0.0,  hex: '#ffd700' },   // gold
  { r: 0.29, g: 0.87, b: 0.50, hex: '#4ade80' },   // green
  { r: 0.38, g: 0.56, b: 1.0,  hex: '#618fff' },    // blue
  { r: 1.0,  g: 0.45, b: 0.0,  hex: '#ff7300' },    // deep orange
  { r: 0.68, g: 0.45, b: 0.98, hex: '#ad73fa' },    // purple
  { r: 0.22, g: 0.78, b: 0.82, hex: '#38c7d1' },    // teal
  { r: 0.96, g: 0.46, b: 0.68, hex: '#f575ae' },    // pink
  { r: 0.50, g: 0.82, b: 0.33, hex: '#80d154' },    // lime
  { r: 0.94, g: 0.33, b: 0.31, hex: '#f05450' },    // red
  { r: 0.85, g: 0.29, b: 0.72, hex: '#d94ab8' },    // magenta
  { r: 0.22, g: 0.60, b: 1.0,  hex: '#3899ff' },    // sky blue
  { r: 0.60, g: 0.90, b: 0.20, hex: '#99e633' },    // chartreuse
  { r: 0.75, g: 0.60, b: 0.40, hex: '#bf9966' },    // tan
  { r: 0.40, g: 0.30, b: 0.90, hex: '#664de6' },    // indigo
];

/** Subtle white/gray palette for baseline multi-flight. */
const BASELINE_MULTI_COLORS: ComparisonColor[] = [
  { r: 1.0,  g: 1.0,  b: 1.0,  hex: '#ffffff' },
  { r: 0.90, g: 0.90, b: 0.90, hex: '#e6e6e6' },
  { r: 0.80, g: 0.80, b: 0.80, hex: '#cccccc' },
  { r: 0.95, g: 0.95, b: 0.95, hex: '#f2f2f2' },
  { r: 0.85, g: 0.85, b: 0.85, hex: '#d9d9d9' },
  { r: 1.0,  g: 1.0,  b: 1.0,  hex: '#ffffff' },
  { r: 0.90, g: 0.90, b: 0.90, hex: '#e6e6e6' },
  { r: 0.80, g: 0.80, b: 0.80, hex: '#cccccc' },
  { r: 0.95, g: 0.95, b: 0.95, hex: '#f2f2f2' },
  { r: 0.85, g: 0.85, b: 0.85, hex: '#d9d9d9' },
];

const BASELINE_COLOR = { r: 1, g: 1, b: 1, hex: '#ffffff' };

const NUM_COMPARISONS = 10;
const NUM_SPEC_COMPARISONS = 25;
const MAX_BALL_CONFIGS = 14;
const SHOTS_PER_CONFIG = 1;
const SPEC_TRANCHE_SIZE = 3;   // Fire specs in batches of 3 — lower peak GPU load for iOS
const GEAR_TRANCHE_SIZE = 3;   // Same peak-shot cap for iPhone Safari stability
const BALL_TRANCHE_SIZE = 3;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Map ShaftFlex letters to readable names. */
const FLEX_NAMES: Record<string, string> = {
  'L': 'Ladies', 'A': 'Senior', 'R': 'Regular', 'S': 'Stiff', 'X': 'Extra Stiff',
};

/** Map HeadType to display names. */
const HEAD_TYPE_NAMES: Record<string, string> = {
  'standard': 'Standard', 'draw': 'Draw', 'fade': 'Fade',
  'low-spin': 'Low-Spin', 'max-forgiveness': 'Max Forgiveness',
};

/** Map LaunchProfile to display names. */
const LAUNCH_PROFILE_NAMES: Record<string, string> = {
  'low': 'Low', 'mid-low': 'Mid-Low', 'mid': 'Mid', 'mid-high': 'Mid-High', 'high': 'High',
};

/** Map WeightSetting to display names. */
const WEIGHT_SETTING_NAMES: Record<string, string> = {
  'standard': 'Standard', 'draw': 'Draw', 'fade': 'Fade', 'back': 'Back', 'front': 'Front',
};

/** SHORT summary for leaderboard: loft, stiffness, shaft weight, head type (if not standard). */
function buildSpecSummary(specs: GearSpecs, _clubKey?: string): string {
  const parts: string[] = [];
  parts.push(`${specs.loft}°`);
  parts.push(FLEX_NAMES[specs.shaftFlex] || specs.shaftFlex);
  parts.push(`${specs.shaftWeight}g`);
  if (specs.headType && specs.headType !== 'standard') {
    parts.push(HEAD_TYPE_NAMES[specs.headType] || specs.headType);
  }
  return parts.join(' / ');
}

/** FULL detail for spec panel — returns HTML spec grid with all parameters. */
export function buildSpecDetail(specs: GearSpecs, _clubKey?: string): string {
  const cards: { value: string; label: string }[] = [
    { value: `${specs.clubLength}"`, label: 'Length' },
    { value: `${specs.loft}°`, label: 'Head Loft' },
    { value: specs.swingWeight, label: 'Swing Weight' },
    { value: LAUNCH_PROFILE_NAMES[specs.launchProfile] || specs.launchProfile, label: 'Launch Profile' },
    { value: FLEX_NAMES[specs.shaftFlex] || specs.shaftFlex, label: 'Stiffness' },
    { value: `${specs.shaftWeight}g`, label: 'Shaft Weight' },
    { value: HEAD_TYPE_NAMES[specs.headType] || specs.headType, label: 'Head Type' },
    { value: WEIGHT_SETTING_NAMES[specs.weightSetting] || specs.weightSetting, label: 'Weight Setting' },
  ];
  const cardHtml = cards.map(c =>
    `<div class="m-sd-spec-card"><strong>${c.value}</strong><span>${c.label}</span></div>`
  ).join('');
  return `<div class="m-sd-spec-grid">${cardHtml}</div>`;
}

/** Build a human-readable summary for a golf ball (e.g., "Urethane · 87 comp · 3-pc"). */
function buildBallSummary(ball: GolfBall): string {
  const cover = ball.cover.charAt(0).toUpperCase() + ball.cover.slice(1);
  const layers = ball.ball?.layers ? `${ball.ball.layers}-pc` : '';
  const parts = [cover, `${ball.compression} comp`, layers].filter(Boolean);
  return parts.join(' · ');
}

/** Average numeric values from an array of formatted stat records. */
function computeAverageFormatted(allFormatted: Record<string, string>[], spinRates?: number[]): Record<string, string> {
  if (allFormatted.length === 0) return {};

  const numericKeys = ['carry', 'total', 'height', 'time', 'landing', 'offline'];
  const avg: Record<string, string> = {};

  for (const key of numericKeys) {
    let sum = 0;
    let count = 0;
    for (const f of allFormatted) {
      const v = parseFloat(f[key]);
      if (!isNaN(v)) {
        sum += v;
        count++;
      }
    }
    avg[key] = count > 0 ? (sum / count).toFixed(1) : '--';
  }

  // offlineDir: pick the most common direction
  const dirs = allFormatted.map(f => f.offlineDir || '').filter(d => d !== '');
  const leftCount = dirs.filter(d => d === 'L').length;
  const rightCount = dirs.filter(d => d === 'R').length;
  avg.offlineDir = leftCount >= rightCount ? 'L' : 'R';

  // Average spin rate from per-shot input values
  if (spinRates && spinRates.length > 0) {
    const avgSpin = spinRates.reduce((a, b) => a + b, 0) / spinRates.length;
    const spinDisplay = avgSpin >= 1000 ? (avgSpin / 1000).toFixed(1) + 'k' : String(Math.round(avgSpin));
    avg.spin = spinDisplay;
  }

  return avg;
}

// ── Orchestrator ─────────────────────────────────────────────────────────────

export class GearTestOrchestrator {
  private tracer: ShotTracer;
  private scene: Scene;
  private shadowGen: CascadedShadowGenerator;

  private _phase = TestPhase.Ready;

  // Baseline data
  private clubKey = 'driver';
  private baseline: ClubPreset | null = null;

  // Active comparison shots (10 parallel)
  private shots: ComparisonShot[] = [];
  private shotSpecs: { specs: GearSpecs; color: ComparisonColor; label: string }[] = [];

  // Results (including baseline as first entry)
  private results: ComparisonEntry[] = [];

  // Track which shots have been reported as complete (for progressive updates)
  private reportedShots = new Set<number>();

  // ── Multi-shot-per-config tracking ──
  /** Maps config index → array of shot indices in this.shots[] */
  private shotsByConfig: Map<number, number[]> = new Map();
  /** Tracks which configs have been fully reported (all SHOTS_PER_CONFIG done) */
  private reportedConfigs = new Set<number>();
  /** Same for gear configs */
  private reportedGearConfigs = new Set<number>();
  /** Maps config index → array of shot indices for gear shots */
  private gearShotsByConfig: Map<number, number[]> = new Map();

  // ── Multi-baseline state ──
  private baselineShots: ComparisonShot[] = [];
  private reportedBaselineShots = new Set<number>();
  private baselineVariants: SwingParams[] = [];

  // ── Gear testing state ──
  private gearBags: { bag: EquipmentBag; color: ComparisonColor; label: string }[] = [];
  private gearResults: ComparisonEntry[] = [];
  private gearBaseline: ComparisonEntry | null = null;
  private reportedGearShots = new Set<number>();
  private selectedSpecs: GearSpecs | null = null;
  private inFlightFrameCounter = 0;
  private gearTrancheIndex = 0;
  private gearTrancheLaunched = 0;
  private gearTrancheShotStart = 0;

  // ── Spec tranche state (fire in batches of SPEC_TRANCHE_SIZE) ──
  private specTrancheIndex = 0;    // Which tranche we're on (0-based)
  private specTrancheLaunched = 0; // Total configs launched so far
  private specTrancheShotStart = 0; // Index into this.shots where current tranche begins

  // ── Ball testing state ──
  private ballConfigs: { ball: GolfBall; color: ComparisonColor; label: string }[] = [];
  private ballResults: ComparisonEntry[] = [];
  private ballBaseline: ComparisonEntry | null = null;
  private reportedBallShots = new Set<number>();
  private ballShotsByConfig: Map<number, number[]> = new Map();
  private reportedBallConfigs = new Set<number>();
  private selectedGearBag: EquipmentBag | null = null;
  private ballTrancheIndex = 0;
  private ballTrancheLaunched = 0;
  private ballTrancheShotStart = 0;
  // ── Summary state ──
  private summaryData: { baseline: ComparisonEntry; bestSpec: ComparisonEntry; bestGear: ComparisonEntry; bestBall?: ComparisonEntry } | null = null;

  // ── ELDRICK flow state ──
  private eldrickPayload: EldrickPayload | null = null;
  private eldrickLaunch: SwingParams | null = null;
  private baselineReference: SwingParams | null = null;
  private patternRunner: PatternRunner | null = null;
  private patterns = new Map<string, ShotPattern>();
  private pendingPatterns = new Set<string>();
  private pairedSwings: SwingParams[] = [];
  private patternSource: PatternSource = 'modeled';
  private patternEpoch = 0;
  private review: ComparisonReview | null = null;
  private _isEldrickFlow = false;

  // ── Callbacks for UIManager ──
  onPhaseChange: ((phase: TestPhase) => void) | null = null;
  onFlightFramingChanged: ((points: Vec3[]) => void) | null = null;

  getFlightFramingPoints(): Vec3[] {
    const baselinePhase = [TestPhase.BaselineFlight, TestPhase.BaselineMultiFlight, TestPhase.BaselineDone].includes(this._phase);
    return [...(baselinePhase ? this.baselineShots : this.shots).flatMap(shot => shot.getFramingPoints()),
      ...(this.review?.getFramingPoints() ?? [])];
  }
  onComparisonProgress: ((current: number, total: number) => void) | null = null;
  onAllComplete: ((results: ComparisonEntry[]) => void) | null = null;
  /** Called for each comparison shot so UIManager can record it in the shot log. */
  onShotComplete: ((formatted: Record<string, string>) => void) | null = null;
  /** Called when a single comparison shot finishes, with its index and full entry. */
  onSingleShotResult: ((index: number, entry: ComparisonEntry) => void) | null = null;
  /** Called when all gear shots complete. */
  onAllGearComplete: ((results: ComparisonEntry[]) => void) | null = null;
  /** Called when a single gear shot finishes. */
  onGearShotResult: ((index: number, entry: ComparisonEntry) => void) | null = null;
  /** Called when all ball shots complete. */
  onAllBallComplete: ((results: ComparisonEntry[]) => void) | null = null;
  /** Called when a single ball shot finishes. */
  onBallShotResult: ((index: number, entry: ComparisonEntry) => void) | null = null;
  /** Called when all baseline multi-shots complete, with averaged formatted stats. */
  onAllBaselineComplete: ((avgFormatted: Record<string, string>) => void) | null = null;
  /** Called when a single baseline shot completes (for progress tracking). */
  onBaselineShotProgress: ((completed: number, total: number) => void) | null = null;
  /** Called when a single baseline shot finishes with its formatted data (for real-time table). */
  onBaselineSingleShotResult: ((index: number, formatted: Record<string, string>, spinRate: number) => void) | null = null;
  /** Called every few frames with in-flight carry data for real-time leaderboard. */
  onInFlightCarry: ((data: { index: number; label: string; colorHex: string; carryYards: number; spinRate: number; specSummary: string; inFlight: boolean; offline: number }[]) => void) | null = null;

  /** Called when all shots land with their rest positions + phase type (for camera). */
  onShotsLanded: ((positions: Vec3[], phase: 'spec' | 'gear' | 'ball') => void) | null = null;

  /** When true, pause at BaselineDone instead of auto-advancing to Comparing. */
  deferComparing = false;

  /** Whether this session was initiated from ELDRICK fitting data. */
  get isEldrickFlow(): boolean { return this._isEldrickFlow; }
  get fittingObjective() { return this.eldrickPayload?.meta.objective ?? 'balanced'; }

  /** Get the ELDRICK payload (if in ELDRICK flow). */
  getEldrickPayload(): EldrickPayload | null { return this.eldrickPayload; }

  constructor(
    tracer: ShotTracer,
    scene: Scene,
    shadowGen: CascadedShadowGenerator,
    private environmentProvider: () => EnvironmentConfig = () => ({ ...DEFAULT_ENVIRONMENT }),
  ) {
    this.tracer = tracer;
    this.scene = scene;
    this.shadowGen = shadowGen;
  }

  // Keep baseline/spec/gear/ball tranches in one comparison under one atmosphere.
  private comparisonEnvironment: EnvironmentConfig = { ...DEFAULT_ENVIRONMENT };

  get phase(): TestPhase {
    return this._phase;
  }

  getClubKey(): string {
    return this.clubKey;
  }

  /** Get the baseline entry (results[0]) for delta calculations. */
  getBaselineEntry(): ComparisonEntry | null {
    return this.results.length > 0 ? this.results[0] : null;
  }

  /** Get the gear baseline entry for gear delta calculations. */
  getGearBaselineEntry(): ComparisonEntry | null {
    return this.gearBaseline;
  }

  /** Get config metadata for building skeleton table rows (colors, labels, summaries). */
  getConfigMeta(): { color: ComparisonColor; label: string; specSummary: string }[] {
    return this.shotSpecs.map(s => ({
      color: s.color,
      label: s.label,
      specSummary: buildSpecSummary(s.specs, this.clubKey),
    }));
  }

  /** Get gear config metadata for building gear skeleton table rows. */
  getGearConfigMeta(): { color: ComparisonColor; label: string; specSummary: string }[] {
    return this.gearBags.map(g => ({
      color: g.color,
      label: g.label,
      specSummary: g.label,
    }));
  }

  /** Get gear bag at index. */
  getGearBag(index: number): EquipmentBag | null {
    return this.gearBags[index]?.bag || null;
  }

  /** Get summary data for the final comparison view. */
  getSummaryData(): { baseline: ComparisonEntry; bestSpec: ComparisonEntry; bestGear: ComparisonEntry; bestBall?: ComparisonEntry } | null {
    return this.summaryData;
  }

  /** Get the selected spec config used for gear testing. */
  getSelectedSpecs(): GearSpecs | null {
    return this.selectedSpecs;
  }

  /** Get all gear bags generated for gear testing. */
  getGearBags(): { bag: EquipmentBag; label: string }[] {
    return this.gearBags.map(g => ({ bag: g.bag, label: g.label }));
  }

  /** Get all gear comparison results (including spec baseline as first entry). */
  getGearResults(): ComparisonEntry[] {
    return this.gearResults;
  }

  /** Get all spec comparison results (including baseline as first entry). */
  getResults(): ComparisonEntry[] {
    return this.results;
  }

  /** Number of spec comparisons (for UIManager progress display). */
  getSpecCount(): number {
    return NUM_SPEC_COMPARISONS;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Called when user clicks HIT BASELINE.
   * Just transitions phase — the actual shot is fired by UIManager.
   */
  startBaseline(clubKey: string, baseline: ClubPreset): void {
    this.resetPatterns();
    this.baselineReference = null;
    this.comparisonEnvironment = { ...this.environmentProvider() };
    this.clubKey = clubKey;
    this.baseline = baseline;
    this.results = [];
    this.gearResults = [];
    this.gearBaseline = null;
    this.summaryData = null;
    this.disposeShots();
    this.disposeBaselineShots();
    this._phase = TestPhase.BaselineFlight;
    this.tracer.resetColor();
    this.onPhaseChange?.(this._phase);
  }

  /**
   * Start from ELDRICK fitting data — skip baseline entirely.
   * Builds a synthetic baseline from the swing data, generates neighborhood specs
   * around the recommendation, and jumps straight to the Comparing phase.
   */
  startFromEldrick(payload: EldrickPayload): void {
    this.resetPatterns();
    this.comparisonEnvironment = { ...this.environmentProvider() };
    this._isEldrickFlow = true;
    this.eldrickPayload = payload;

    // Determine club key from fitting type
    const fittingType = payload.meta.fittingType || 'driver';
    this.clubKey = fittingType === 'iron' ? '7iron' : 'driver';

    this.results = [];
    this.gearResults = [];
    this.gearBaseline = null;
    this.summaryData = null;
    this.disposeShots();
    this.disposeBaselineShots();

    const launch = importedLaunch(payload);
    this.baseline = launch.baseline;
    this.eldrickLaunch = launch.params;
    this.eldrickBaselineResolved = false;
    const baseParams = launch.params;
    const rawHandicap = payload.player.handicap;
    const handicap = rawHandicap !== null && String(rawHandicap).trim() !== '' && Number.isFinite(Number(rawHandicap)) ? Number(rawHandicap) : 15;
    this.pairedSwings = payload.swingSamples?.map(swing => importedLaunch({ ...payload, swing }).params)
      ?? generateVariantParams(baseParams, handicap, 24, seededPatternRandom(20260905));
    this.patternSource = payload.swingSamples ? 'measured-launches' : 'modeled';
    this.queuePattern('baseline', this.pairedSwings);
    const { clubSpeed, spinRate } = baseParams;

    // Fire a single shot to get carry/total for the baseline entry
    const baselineShot = new ComparisonShot(this.scene, this.shadowGen, BASELINE_COLOR, this.comparisonEnvironment);
    baselineShot.launch(baseParams);
    this.baselineShots.push(baselineShot);

    // Store the baseline entry with placeholder formatted data — will be updated when shot lands
    const spinDisplay = spinRate >= 1000
      ? (spinRate / 1000).toFixed(1) + 'k'
      : String(Math.round(spinRate));

    this.results.push({
      label: 'ELDRICK Baseline',
      specs: null,
      color: { r: BASELINE_COLOR.r, g: BASELINE_COLOR.g, b: BASELINE_COLOR.b },
      colorHex: BASELINE_COLOR.hex,
      formatted: {
        carry: '--', total: '--', height: '--', time: '--',
        landing: '--', offline: '0.0', offlineDir: '', spin: spinDisplay,
      },
      specSummary: 'ELDRICK Capture',
      spinRate,
    });

    // Generate neighborhood specs around the ELDRICK recommendation
    // Map gear fields to neighborhood anchor, with null fallbacks
    const gear = payload.gear;
    const flexMap: Record<string, string> = {
      'Ladies': 'L', 'Senior': 'A', 'Regular': 'R', 'Stiff': 'S', 'Extra Stiff': 'X',
      'L': 'L', 'A': 'A', 'R': 'R', 'S': 'S', 'X': 'X',
    };
    const anchorFlex = flexMap[gear.flex || ''] || 'S';
    const anchorCompression = unwrapValue(payload.currentSetup.ballCompression) ?? 90;

    const neighborhoodSpecs = generateNeighborhoodSpecs(
      {
        loft: gear.loft ?? (this.clubKey === '7iron' ? 34 : 10.5),
        shaftWeight: gear.shaftWeight ?? (this.clubKey === '7iron' ? 100 : 62),
        shaftFlex: anchorFlex,
        compression: anchorCompression,
        details: importedSpecDetails(payload),
      },
      NUM_SPEC_COMPARISONS,
      this.clubKey,
      clubSpeed,
    );

    this.shotSpecs = neighborhoodSpecs.map((specs, i) => ({
      specs,
      color: SPEC_COLORS[i % SPEC_COLORS.length],
      label: i === 0 ? 'ELDRICK Rec' : `Config ${String.fromCharCode(65 + i)}`,
    }));

    // Transition to BaselineDone first so UI can show the ELDRICK capture card,
    // then proceed to Comparing (respects deferComparing for mobile)
    this._phase = TestPhase.BaselineDone;
    this.onPhaseChange?.(this._phase);

    this.onFlightFramingChanged?.(this.getFlightFramingPoints());

    if (!this.deferComparing) {
      setTimeout(() => {
        this._phase = TestPhase.Comparing;
        this.onPhaseChange?.(this._phase);
        this.launchAllComparisons();
      }, 800);
    }
  }

  /**
   * Start multi-baseline: fire 10 shots with variance simultaneously.
   * Called after the single baseline shot lands.
   */
  startMultiBaseline(clubKey: string, baseline: ClubPreset, params: SwingParams, handicap: number): void {
    this.resetPatterns();
    this.comparisonEnvironment = { ...this.environmentProvider() };
    this.clubKey = clubKey;
    this.baseline = baseline;
    this.reportedBaselineShots.clear();
    this.disposeBaselineShots();

    // Generate 10 variant params
    const variants = generateVariantParams(params, handicap, NUM_COMPARISONS, seededPatternRandom(20260905));
    this.baselineVariants = variants;
    this.pairedSwings = variants;
    this.queuePattern('baseline', variants);

    // Launch 10 baseline shots with subtle white/gray colors
    for (let i = 0; i < variants.length; i++) {
      const color = BASELINE_MULTI_COLORS[i % BASELINE_MULTI_COLORS.length];
      const shot = new ComparisonShot(this.scene, this.shadowGen, color, this.comparisonEnvironment);
      shot.launch(variants[i]);
      this.baselineShots.push(shot);
    }

    this._phase = TestPhase.BaselineMultiFlight;
    this.onPhaseChange?.(this._phase);
    this.onFlightFramingChanged?.(this.getFlightFramingPoints());
  }

  /**
   * Called by UIManager's onComplete handler when the baseline shot lands.
   * Stores baseline result, generates comparison specs, launches all simultaneously.
   */
  onBaselineComplete(formatted: Record<string, string>): void {
    if (this._phase !== TestPhase.BaselineFlight && this._phase !== TestPhase.BaselineMultiFlight) return;

    // Store baseline result
    this.results.push({
      label: 'Baseline',
      specs: null,
      color: { r: BASELINE_COLOR.r, g: BASELINE_COLOR.g, b: BASELINE_COLOR.b },
      colorHex: BASELINE_COLOR.hex,
      formatted,
      specSummary: 'Stock',
      spinRate: this.baseline?.spinRate || 0,
    });
    this.attachPattern(this.results[0], 'baseline');

    this._phase = TestPhase.BaselineDone;
    this.onPhaseChange?.(this._phase);

    // Generate spec configs seeded to this player's swing speed
    const specSets = generateSpreadSpecSet(NUM_SPEC_COMPARISONS, this.baseline?.clubSpeed);
    this.shotSpecs = specSets.map((specs, i) => ({
      specs,
      color: SPEC_COLORS[i % SPEC_COLORS.length],
      label: `Config ${String.fromCharCode(65 + i)}`,   // A, B, C, ..., Y
    }));

    if (this.deferComparing) {
      // Mobile: wait for manual proceedToComparing() call
      return;
    }

    // Desktop: launch all after brief pause
    setTimeout(() => {
      this._phase = TestPhase.Comparing;
      this.onPhaseChange?.(this._phase);
      this.launchAllComparisons();
    }, 800);
  }

  /**
   * Manually advance from BaselineDone → Comparing.
   * Used on mobile after user reviews the baseline stats.
   */
  proceedToComparing(): void {
    if (this._phase !== TestPhase.BaselineDone) return;
    this._phase = TestPhase.Comparing;
    this.onPhaseChange?.(this._phase);
    this.launchAllComparisons();
  }

  /**
   * Start gear testing with the selected spec config's adjusted launch data.
   * Takes the selected spec entry, generates 10 random equipment bags,
   * and fires them in small tranches for mobile stability.
   */
  startGearTest(specEntry: ComparisonEntry): void {
    if (!this.baseline) return;

    // Store the spec entry as gear baseline for delta calculation
    this.gearBaseline = specEntry;
    this.selectedSpecs = specEntry.specs;
    this.gearResults = [];
    this.reportedGearShots.clear();
    this.gearTrancheIndex = 0;
    this.gearTrancheLaunched = 0;
    this.gearTrancheShotStart = 0;

    // Add the spec entry as the first result (gear baseline)
    this.gearResults.push({
      ...specEntry,
      recommendationSource: undefined,
      label: 'Spec Baseline',
      colorHex: BASELINE_COLOR.hex,
      color: { r: BASELINE_COLOR.r, g: BASELINE_COLOR.g, b: BASELINE_COLOR.b },
    });

    // Dispose old comparison shots from the spec phase
    this.disposeShots();

    // Generate random gear bags filtered to this player's speed
    const bags = generateRandomGearBags(NUM_COMPARISONS, this.clubKey, this.baseline?.clubSpeed);
    this.gearBags = bags.map((bag, i) => ({
      bag,
      color: GEAR_COLORS[i % GEAR_COLORS.length],
      label: buildGearLabel(bag, this.clubKey),
    }));

    this._phase = TestPhase.GearTesting;
    this.onPhaseChange?.(this._phase);

    this.launchGearTranche();
  }

  /**
   * Transition to the Summary phase — shows baseline vs best spec vs best gear comparison.
   * Called when user taps VIEW RESULTS from gear results.
   */
  /**
   * Start ball testing with the selected gear entry.
   * Takes all non-avg balls from the library, fires them with the selected
   * spec + gear combo, producing a full ball comparison.
   */
  startBallTest(gearEntry: ComparisonEntry): void {
    if (!this.baseline) return;

    // Store the gear entry as ball baseline for delta calculation
    this.ballBaseline = gearEntry;
    this.ballResults = [];
    this.reportedBallShots.clear();
    this.ballShotsByConfig.clear();
    this.reportedBallConfigs.clear();
    this.ballTrancheIndex = 0;
    this.ballTrancheLaunched = 0;
    this.ballTrancheShotStart = 0;

    // Add gear entry as first result (ball baseline)
    this.ballResults.push({
      ...gearEntry,
      recommendationSource: undefined,
      label: 'Gear Baseline',
      colorHex: BASELINE_COLOR.hex,
      color: { r: BASELINE_COLOR.r, g: BASELINE_COLOR.g, b: BASELINE_COLOR.b },
    });

    // Dispose old gear shots
    this.disposeShots();

    // Find the matching gear bag for the selected gear entry.
    // If none (e.g. "Spec Baseline"), keep this null so ball testing
    // runs with neutral head/shaft and only applies selected specs + test ball.
    const matchedBag = this.gearBags.find(g => g.label === gearEntry.label);
    this.selectedGearBag = matchedBag ? matchedBag.bag : null;

    // Use non-avg balls from the library, capped at MAX_BALL_CONFIGS for browser perf
    const balls = GOLF_BALLS.filter(b => b.id !== 'avg').slice(0, MAX_BALL_CONFIGS);
    this.ballConfigs = balls.map((ball, i) => ({
      ball,
      color: BALL_COLORS[i % BALL_COLORS.length],
      label: ball.brand && ball.brand !== '--' ? `${ball.brand} ${ball.name}` : ball.name,
    }));

    this._phase = TestPhase.BallTesting;
    this.onPhaseChange?.(this._phase);

    // Launch ball shots in small tranches for mobile stability
    this.launchBallTranche();
  }

  /** Get ball config metadata for building ball skeleton table rows. */
  getBallConfigMeta(): { color: ComparisonColor; label: string; specSummary: string }[] {
    return this.ballConfigs.map(b => ({
      color: b.color,
      label: b.label,
      specSummary: buildBallSummary(b.ball),
    }));
  }

  /** Get the ball baseline entry for delta calculations. */
  getBallBaselineEntry(): ComparisonEntry | null {
    return this.ballBaseline;
  }

  /** Get all ball comparison results. */
  getBallResults(): ComparisonEntry[] {
    return this.ballResults;
  }

  transitionToSummary(bestSpec: ComparisonEntry, bestGear: ComparisonEntry, bestBall?: ComparisonEntry): void {
    const baseline = this.results[0]; // baseline is always results[0]
    if (!baseline) return;

    this.summaryData = { baseline, bestSpec, bestGear, bestBall };

    this._phase = TestPhase.Summary;
    this.onPhaseChange?.(this._phase);
  }

  /** Reset everything back to READY state. */
  reset(): void {
    this.resetPatterns();
    this.disposeShots();
    this.disposeBaselineShots();
    this.tracer.clearAll();
    this.tracer.resetColor();
    this.baseline = null;
    this.results = [];
    this.shotSpecs = [];
    this.reportedShots.clear();
    this.shotsByConfig.clear();
    this.reportedConfigs.clear();
    this.gearResults = [];
    this.gearBags = [];
    this.gearBaseline = null;
    this.selectedSpecs = null;
    this.reportedGearShots.clear();
    this.gearShotsByConfig.clear();
    this.reportedGearConfigs.clear();
    this.gearTrancheIndex = 0;
    this.gearTrancheLaunched = 0;
    this.gearTrancheShotStart = 0;
    this.ballResults = [];
    this.ballConfigs = [];
    this.ballBaseline = null;
    this.selectedGearBag = null;
    this.reportedBallShots.clear();
    this.ballShotsByConfig.clear();
    this.reportedBallConfigs.clear();
    this.ballTrancheIndex = 0;
    this.ballTrancheLaunched = 0;
    this.ballTrancheShotStart = 0;
    this.summaryData = null;
    this.reportedBaselineShots.clear();
    this._isEldrickFlow = false;
    this.eldrickPayload = null;
    this.eldrickLaunch = null;
    this.baselineReference = null;
    this.eldrickBaselineResolved = false;
    this._phase = TestPhase.Ready;
    this.onPhaseChange?.(this._phase);
  }

  // ── Physics + Render Update (called by game loop) ─────────────────────────

  /** Step all active comparison shots. Call once per physics tick. */
  physicsUpdate(dt: number): void {
    if (this._phase === TestPhase.BaselineMultiFlight) {
      this.updateBaselineShots(dt);
    } else if (this._isEldrickFlow && !this.eldrickBaselineResolved && this.baselineShots.length > 0) {
      // ELDRICK flow: step the single baseline shot to get distance data (any phase)
      this.updateEldrickBaselineShot(dt);
    } else if (this._phase === TestPhase.Comparing) {
      this.updateSpecShots(dt);
    } else if (this._phase === TestPhase.GearTesting) {
      this.updateGearShots(dt);
    } else if (this._phase === TestPhase.BallTesting) {
      this.updateBallShots(dt);
    }
  }

  /** Render all active comparison shots. Call once per render frame. */
  renderUpdate(alpha: number): void {
    this.review?.update();
    // Render baseline multi-shots (or ELDRICK single baseline shot)
    if (this._phase === TestPhase.BaselineMultiFlight ||
        (this._phase === TestPhase.BaselineDone && this._isEldrickFlow)) {
      for (const shot of this.baselineShots) {
        shot.renderUpdate(alpha);
      }
      if (this._phase === TestPhase.BaselineMultiFlight) return;
    }

    if (this._phase !== TestPhase.Comparing &&
        this._phase !== TestPhase.GearTesting &&
        this._phase !== TestPhase.BallTesting) return;

    for (const shot of this.shots) {
      shot.renderUpdate(alpha);
    }
  }

  // ── Private: Multi-baseline shots ─────────────────────────────────────────

  private updateBaselineShots(dt: number): void {
    for (let i = 0; i < this.baselineShots.length; i++) {
      const shot = this.baselineShots[i];
      if (!shot.isComplete()) {
        shot.physicsUpdate(dt);
      }
      // Track per-shot completion for progress + real-time table
      if (shot.isComplete() && !this.reportedBaselineShots.has(i)) {
        this.reportedBaselineShots.add(i);
        const data = shot.getResult();
        if (data) {
          const spinRate = this.baselineVariants[i]?.spinRate || this.baseline?.spinRate || 0;
          this.onBaselineSingleShotResult?.(i, data.formatted, spinRate);
        }
        this.onBaselineShotProgress?.(this.reportedBaselineShots.size, this.baselineShots.length);
      }
    }

    // Check if all baseline shots are done
    if (this.baselineShots.length > 0 && this.baselineShots.every(s => s.isComplete()) && !this.pendingPatterns.has('baseline')) {
      // Compute average formatted stats from all completed shots
      const allFormatted: Record<string, string>[] = [];
      for (const shot of this.baselineShots) {
        const data = shot.getResult();
        if (data) allFormatted.push(data.formatted);
      }

      // Collect per-shot spin rates from the variant params
      const spinRates = this.baselineVariants.map(v => v.spinRate);
      const avgFormatted = computeAverageFormatted(allFormatted, spinRates);

      // Compare the same representative delivery, including the player's miss.
      this.averageBaselineAsReference();

      this.onAllBaselineComplete?.(avgFormatted);

      // Now call onBaselineComplete with the averaged stats
      this.onBaselineComplete(avgFormatted);
    }
  }

  // ── Private: ELDRICK single baseline shot ──────────────────────────────────

  private eldrickBaselineResolved = false;

  private updateEldrickBaselineShot(dt: number): void {
    if (this.eldrickBaselineResolved) return;
    for (const shot of this.baselineShots) {
      if (!shot.isComplete()) shot.physicsUpdate(dt);
    }
    // When the shot lands, capture its formatted data and update results[0]
    if (this.baselineShots.length > 0 && this.baselineShots[0].isComplete() && !this.pendingPatterns.has('baseline')) {
      this.eldrickBaselineResolved = true;
      const data = this.baselineShots[0].getResult();
      if (data && this.results[0]) {
        this.results[0].formatted = {
          ...data.formatted,
          spin: this.results[0].formatted.spin, // keep the ELDRICK spin display
        };
        this.attachPattern(this.results[0], 'baseline');
        // Notify UI with the resolved baseline stats
        this.onAllBaselineComplete?.(this.results[0].formatted);
      }
    }
  }

  // ── Private: Spec comparison shots ──────────────────────────────────────────

  private updateSpecShots(dt: number): void {
    // Step all individual shots
    for (let i = 0; i < this.shots.length; i++) {
      const shot = this.shots[i];
      if (!shot.isComplete()) {
        shot.physicsUpdate(dt);
      }
    }

    // Check each config: when all SHOTS_PER_CONFIG shots complete, aggregate and report
    for (const [configIdx, shotIndices] of this.shotsByConfig) {
      if (this.reportedConfigs.has(configIdx)) continue;
      if (this.pendingPatterns.has(`spec:${configIdx}`) || this.pendingPatterns.has('baseline')) continue;

      const allDone = shotIndices.every(si => this.shots[si].isComplete());
      if (!allDone) continue;

      this.reportedConfigs.add(configIdx);
      const entry = this.buildAggregatedSpecEntry(configIdx);
      if (entry) {
        this.attachPattern(entry, `spec:${configIdx}`);
        if (this._isEldrickFlow && configIdx === 0) entry.recommendationSource = 'core';
        entry.configIndex = configIdx;
        this.results.push(entry);
        this.onShotComplete?.(entry.formatted);
        this.onSingleShotResult?.(configIdx, entry);
      }
    }

    // Emit in-flight carry data every ~10 physics frames for real-time leaderboard
    this.inFlightFrameCounter++;
    if (this.onInFlightCarry && this.inFlightFrameCounter % 10 === 0) {
      this.emitInFlightCarry('spec');
    }

    // Check if current tranche is done (only check shots from current tranche)
    const trancheShots = this.shots.slice(this.specTrancheShotStart);
    if (trancheShots.length > 0 && trancheShots.every(s => s.isComplete()) && this.reportedConfigs.size === this.specTrancheLaunched) {
      if (this.specTrancheLaunched < this.shotSpecs.length) {
        // Log resource state before/after dispose for crash debugging
        const scene = this.scene;
        const before = { meshes: scene.meshes.length, materials: scene.materials.length, textures: scene.textures.length };
        // Free GPU memory from current tranche's shots only (keep tracer lines visible)
        for (const shot of trancheShots) {
          shot.disposeHeavy();
        }
        const after = { meshes: scene.meshes.length, materials: scene.materials.length, textures: scene.textures.length };
        console.log(`[TRANCHE] ${this.specTrancheIndex} done (${this.specTrancheLaunched}/${this.shotSpecs.length}) | before: ${before.meshes}m/${before.materials}mat/${before.textures}tex → after: ${after.meshes}m/${after.materials}mat/${after.textures}tex`);
        // More tranches to go — launch next batch
        this.launchSpecTranche();
      } else {
        // All tranches complete
        this._phase = TestPhase.Results;
        this.onPhaseChange?.(this._phase);
        this.onAllComplete?.(this.results);
        this.emitLandingPositions('spec');
      }
    }
  }

  // ── Private: Gear testing shots ─────────────────────────────────────────────

  private updateGearShots(dt: number): void {
    // Step all individual shots
    for (let i = 0; i < this.shots.length; i++) {
      const shot = this.shots[i];
      if (!shot.isComplete()) {
        shot.physicsUpdate(dt);
      }
    }

    // Check each config: when all SHOTS_PER_CONFIG shots complete, aggregate and report
    for (const [configIdx, shotIndices] of this.gearShotsByConfig) {
      if (this.reportedGearConfigs.has(configIdx)) continue;
      if (this.pendingPatterns.has(`gear:${configIdx}`)) continue;

      const allDone = shotIndices.every(si => this.shots[si].isComplete());
      if (!allDone) continue;

      this.reportedGearConfigs.add(configIdx);
      const entry = this.buildAggregatedGearEntry(configIdx);
      if (entry) {
        this.attachPattern(entry, `gear:${configIdx}`);
        entry.configIndex = configIdx;
        this.gearResults.push(entry);
        this.onShotComplete?.(entry.formatted);
        this.onGearShotResult?.(configIdx, entry);
      }
    }

    // Emit in-flight carry data every ~10 physics frames for real-time leaderboard
    this.inFlightFrameCounter++;
    if (this.onInFlightCarry && this.inFlightFrameCounter % 10 === 0) {
      this.emitInFlightCarry('gear');
    }

    const trancheShots = this.shots.slice(this.gearTrancheShotStart);
    if (trancheShots.length > 0 && trancheShots.every(s => s.isComplete()) && this.reportedGearConfigs.size === this.gearTrancheLaunched) {
      if (this.gearTrancheLaunched < this.gearBags.length) {
        for (const shot of trancheShots) {
          shot.disposeHeavy();
        }
        this.launchGearTranche();
      } else {
        this._phase = TestPhase.GearResults;
        this.onPhaseChange?.(this._phase);
        this.onAllGearComplete?.(this.gearResults);
        this.emitLandingPositions('gear');
      }
    }
  }

  // ── Private: In-flight carry emission ───────────────────────────────────────

  /** Get current world positions of all active (non-idle) shots — for camera tracking. */
  getActiveBallPositions(): Vec3[] {
    const positions: Vec3[] = [];
    for (const shot of this.shots) {
      const pos = shot.getCurrentPosition();
      if (pos) positions.push(pos);
    }
    return positions;
  }

  /** Get rest positions of all completed shots — for camera auto-framing. */
  getLandingPositions(): Vec3[] {
    const positions: Vec3[] = [];
    for (const shot of this.shots) {
      if (shot.isComplete()) {
        const r = shot.getResult();
        if (r) positions.push(r.result.restPosition);
      }
    }
    return positions;
  }

  /** Emit landing positions to camera for auto-framing. */
  private emitLandingPositions(phase: 'spec' | 'gear' | 'ball' = 'spec'): void {
    if (!this.onShotsLanded) return;
    const positions = this.getLandingPositions();
    if (positions.length > 0) {
      this.onShotsLanded(positions, phase);
    }
  }

  private emitInFlightCarry(type: 'spec' | 'gear' | 'ball'): void {
    const meta = type === 'spec' ? this.shotSpecs : type === 'gear' ? this.gearBags : this.ballConfigs;
    const specMeta = type === 'spec' ? this.shotSpecs : null;
    const configMap = type === 'spec' ? this.shotsByConfig : type === 'gear' ? this.gearShotsByConfig : this.ballShotsByConfig;

    const data: { index: number; label: string; colorHex: string; carryYards: number; spinRate: number; specSummary: string; inFlight: boolean; offline: number }[] = [];

    // Emit one entry per config (averaged across its shots)
    for (const [configIdx, shotIndices] of configMap) {
      let totalCarry = 0;
      let totalSpin = 0;
      let anyInFlight = false;

      for (const si of shotIndices) {
        const shot = this.shots[si];
        totalCarry += shot.getCurrentCarryMeters() * M_TO_YARDS;
        totalSpin += shot.getSpinRate();
        if (shot.isInFlight()) anyInFlight = true;
      }

      // Compute average offline for fully landed configs
      let offline = 0;
      if (!anyInFlight) {
        let totalOffline = 0;
        let offlineCount = 0;
        for (const si of shotIndices) {
          const data = this.shots[si].getResult();
          if (data) {
            const o = parseFloat(data.formatted.offline);
            if (!isNaN(o)) { totalOffline += Math.abs(o); offlineCount++; }
          }
        }
        if (offlineCount > 0) offline = totalOffline / offlineCount;
      }

      const count = shotIndices.length;
      const m = meta[configIdx];
      const specSummary = specMeta?.[configIdx]
        ? buildSpecSummary(specMeta[configIdx].specs, this.clubKey)
        : type === 'ball' && this.ballConfigs[configIdx]
          ? buildBallSummary(this.ballConfigs[configIdx].ball)
          : '';

      data.push({
        index: configIdx,
        label: m?.label || `#${configIdx + 1}`,
        colorHex: m?.color.hex || '#fff',
        carryYards: totalCarry / count,
        spinRate: totalSpin / count,
        specSummary,
        inFlight: anyInFlight,
        offline,
      });
    }

    this.onInFlightCarry?.(data);
  }

  private averageBaselineAsReference(): void {
    if (!this.baseline) return;
    const reference = averageBaselineDelivery(this.baselineVariants);
    if (!reference) return;
    this.baselineReference = reference;
    this.baseline = { ...this.baseline, ...reference };
  }

  // ── Private: Launch spec comparisons in tranches of SPEC_TRANCHE_SIZE ──────

  private launchAllComparisons(): void {
    if (!this.baseline) return;

    this.disposeShots();
    this.disposeBaselineShots();
    this.shots = [];
    this.reportedShots.clear();
    this.shotsByConfig.clear();
    this.reportedConfigs.clear();
    this.specTrancheIndex = 0;
    this.specTrancheLaunched = 0;
    this.specTrancheShotStart = 0;

    // Launch first tranche
    this.launchSpecTranche();
  }

  /** Launch the next batch of SPEC_TRANCHE_SIZE spec configs. */
  private launchSpecTranche(): void {
    if (!this.baseline) return;
    const base = this.baseline;

    const start = this.specTrancheLaunched;
    const end = Math.min(start + SPEC_TRANCHE_SIZE, this.shotSpecs.length);

    // Mark where this tranche's shots begin in the shots array
    this.specTrancheShotStart = this.shots.length;

    for (let configIdx = start; configIdx < end; configIdx++) {
      const entry = this.shotSpecs[configIdx];
      const shotIndices: number[] = [];

      const referenceLength = this._isEldrickFlow && this.clubKey === '7iron' ? 37 : 45.5;
      const mods = computeSpecModifiers(entry.specs, this.clubKey, base.clubSpeed, true, referenceLength);
      const adjusted = applyModifiers(base, mods);
      const delivery = comparisonDelivery(base, this.eldrickLaunch ?? this.baselineReference);

      const baseParams = createSwingParams(
        base.clubSpeed,
        adjusted.ballSpeed,
        adjusted.launchAngle,
        delivery.launchDirection,
        adjusted.spinRate,
        delivery.spinAxis,
        base.attackAngle,
        base.clubPath,
        base.faceAngle,
        delivery.tempo,
        delivery.transition,
      );

      this.queueAdjustedPattern(`spec:${configIdx}`, baseParams);

      for (let s = 0; s < SHOTS_PER_CONFIG; s++) {
        const shot = new ComparisonShot(this.scene, this.shadowGen, entry.color, this.comparisonEnvironment);
        shot.launch(this.representativeParams(baseParams));
        const shotIdx = this.shots.length;
        this.shots.push(shot);
        shotIndices.push(shotIdx);
      }

      this.shotsByConfig.set(configIdx, shotIndices);
    }

    this.specTrancheLaunched = end;
    this.onFlightFramingChanged?.(this.getFlightFramingPoints());
    this.specTrancheIndex++;
    console.log(`[TRANCHE] Launched tranche ${this.specTrancheIndex} (configs ${start}-${end - 1}) | shots: ${this.shots.length} total | scene: ${this.scene.meshes.length}m/${this.scene.materials.length}mat/${this.scene.textures.length}tex`);

    // Report progress: how many configs launched / total
    this.onComparisonProgress?.(this.specTrancheLaunched, this.shotSpecs.length);
  }

  // ── Private: Launch gear shots in tranches ─────────────────────────────────

  private launchGearTranche(): void {
    if (!this.baseline || !this.gearBaseline) return;

    const start = this.gearTrancheLaunched;
    const end = Math.min(start + GEAR_TRANCHE_SIZE, this.gearBags.length);
    this.gearTrancheShotStart = this.shots.length;

    const base = this.baseline;
    // Every configuration uses the same representative player delivery.

    // Gear phase: computeModifiers is the COMPLETE club physics for a named bag.
    // It already covers everything spec mods cover (shaft flex/weight effects via
    // legacy per-model mods + DNA), so we use it directly — no spec stacking.
    // Ball effects skipped here; reserved for ball phase.

    for (let configIdx = start; configIdx < end; configIdx++) {
      const entry = this.gearBags[configIdx];
      const shotIndices: number[] = [];

      const delivery = comparisonDelivery(base, this.eldrickLaunch ?? this.baselineReference);
      const gearMods = computeModifiers(this.clubKey, entry.bag, base.clubSpeed, delivery.tempo, delivery.transition, true, { attackAngle: base.attackAngle });
      const adjusted = applyModifiers(base, gearMods);

      const baseParams = createSwingParams(
        base.clubSpeed,
        adjusted.ballSpeed,
        adjusted.launchAngle,
        delivery.launchDirection,
        adjusted.spinRate,
        delivery.spinAxis,
        base.attackAngle,
        base.clubPath,
        base.faceAngle,
        delivery.tempo,
        delivery.transition,
      );

      this.queueAdjustedPattern(`gear:${configIdx}`, baseParams);

      for (let s = 0; s < SHOTS_PER_CONFIG; s++) {
        const shot = new ComparisonShot(this.scene, this.shadowGen, entry.color, this.comparisonEnvironment);
        shot.launch(this.representativeParams(baseParams));
        const shotIdx = this.shots.length;
        this.shots.push(shot);
        shotIndices.push(shotIdx);
      }

      this.gearShotsByConfig.set(configIdx, shotIndices);
    }

    this.gearTrancheLaunched = end;
    this.onFlightFramingChanged?.(this.getFlightFramingPoints());
    this.gearTrancheIndex++;
    this.onComparisonProgress?.(this.gearTrancheLaunched, this.gearBags.length);
  }

  // ── Private: Build aggregated entries (multi-shot per config) ──────────────

  /** Do not present a single representative flight as zero dispersion. */
  private computeSpread(shotIndices: number[]): { carrySpread: number; offlineSpread: number } | undefined {
    if (shotIndices.length < 2) return undefined;
    const carries: number[] = [];
    const offlines: number[] = [];
    for (const si of shotIndices) {
      const data = this.shots[si].getResult();
      if (!data) continue;
      const c = parseFloat(data.formatted.carry);
      const o = data.result.landingPosition.x * M_TO_YARDS;
      if (!isNaN(c)) carries.push(c);
      if (!isNaN(o)) offlines.push(o);
    }
    return {
      carrySpread: quantile(carries, .9) - quantile(carries, .1),
      offlineSpread: quantile(offlines, .9) - quantile(offlines, .1),
    };
  }

  /** Build an averaged ComparisonEntry for all shots in a spec config. */
  private buildAggregatedSpecEntry(configIdx: number): ComparisonEntry | null {
    const shotIndices = this.shotsByConfig.get(configIdx);
    if (!shotIndices) return null;

    const specEntry = this.shotSpecs[configIdx];
    const allFormatted: Record<string, string>[] = [];
    const spinRates: number[] = [];

    for (const si of shotIndices) {
      const data = this.shots[si].getResult();
      if (data) allFormatted.push(data.formatted);
      spinRates.push(this.shots[si].getSpinRate());
    }

    if (allFormatted.length === 0) return null;

    const avgFormatted = computeAverageFormatted(allFormatted, spinRates);
    const spread = this.computeSpread(shotIndices);

    return {
      label: specEntry.label,
      specs: specEntry.specs,
      color: { r: specEntry.color.r, g: specEntry.color.g, b: specEntry.color.b },
      colorHex: specEntry.color.hex,
      formatted: avgFormatted,
      specSummary: buildSpecSummary(specEntry.specs, this.clubKey),
      spinRate: quantile(spinRates, .5),
      spread,
    };
  }

  /** Build an averaged ComparisonEntry for all shots in a gear config. */
  private buildAggregatedGearEntry(configIdx: number): ComparisonEntry | null {
    const shotIndices = this.gearShotsByConfig.get(configIdx);
    if (!shotIndices) return null;

    const gearEntry = this.gearBags[configIdx];
    const allFormatted: Record<string, string>[] = [];
    const spinRates: number[] = [];

    for (const si of shotIndices) {
      const data = this.shots[si].getResult();
      if (data) allFormatted.push(data.formatted);
      spinRates.push(this.shots[si].getSpinRate());
    }

    if (allFormatted.length === 0) return null;

    const avgFormatted = computeAverageFormatted(allFormatted, spinRates);
    const spread = this.computeSpread(shotIndices);

    return {
      label: gearEntry.label,
      specs: null,   // gear entries don't have GearSpecs
      color: { r: gearEntry.color.r, g: gearEntry.color.g, b: gearEntry.color.b },
      colorHex: gearEntry.color.hex,
      formatted: avgFormatted,
      specSummary: gearEntry.label,
      spinRate: quantile(spinRates, .5),
      spread,
    };
  }

  /**
   * Isolate a config's shots while preserving their identity color.
   * Pass configIndex === -1 to restore original colors.
   * Works for both spec and gear phases via the active shotsByConfig map.
   */
  /**
   * Find the nearest completed shot to a world position (for tap-to-inspect).
   * Returns the config index and formatted shot data, or null if nothing close.
   */
  findNearestShot(worldPos: Vec3, maxDistMeters = 15): { configIndex: number; entry: ComparisonEntry; distance: number } | null {
    const configMap = this.ballShotsByConfig.size > 0 ? this.ballShotsByConfig
      : this.gearShotsByConfig.size > 0 ? this.gearShotsByConfig : this.shotsByConfig;

    let best: { configIndex: number; entry: ComparisonEntry; distance: number } | null = null;

    for (const [configIdx, shotIndices] of configMap) {
      for (const si of shotIndices) {
        const shot = this.shots[si];
        if (!shot.isComplete()) continue;
        const pos = shot.getCurrentPosition();
        if (!pos) continue;

        const dx = pos.x - worldPos.x;
        const dz = pos.z - worldPos.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist < maxDistMeters && (!best || dist < best.distance)) {
          // Get the aggregated entry for this config
          const results = this._phase === TestPhase.BallResults ? this.ballResults
            : this._phase === TestPhase.GearResults ? this.gearResults
            : this.results;
          const entry = results.slice(1).find(result => result.configIndex === configIdx);
          if (entry) {
            best = { configIndex: configIdx, entry, distance: dist };
          }
        }
      }
    }

    return best;
  }

  findShotOnScreen(pointer: ScreenPoint, project: (point: Vec3) => ScreenPoint | null) {
    const map = this._phase === TestPhase.BallResults ? this.ballShotsByConfig
      : this._phase === TestPhase.GearResults ? this.gearShotsByConfig : this.shotsByConfig;
    const results = this._phase === TestPhase.BallResults ? this.ballResults
      : this._phase === TestPhase.GearResults ? this.gearResults : this.results;
    let best: { configIndex: number; entry: ComparisonEntry; distance: number } | null = null;
    for (const [configIndex, indices] of map) {
      const entry = results.slice(1).find(result => result.configIndex === configIndex);
      if (!entry) continue;
      for (const index of indices) {
        let previous: ScreenPoint | null = null;
        for (const point of this.shots[index].getRecordedPositions()) {
          const current = project(point);
          if (current) {
            const distance = distanceToSegment(pointer, previous ?? current, current);
            if (distance <= 22 && (!best || distance < best.distance)) best = { configIndex, entry, distance };
          }
          previous = current;
        }
      }
    }
    return best;
  }

  highlightShot(configIndex: number, _dimAlpha = 0.12): void {
    // Determine which config map is active
    const configMap = this.ballShotsByConfig.size > 0 ? this.ballShotsByConfig
      : this.gearShotsByConfig.size > 0 ? this.gearShotsByConfig : this.shotsByConfig;

    // Build set of shot indices belonging to the highlighted config
    const highlightedIndices = new Set<number>();
    if (configIndex >= 0) {
      const indices = configMap.get(configIndex);
      if (indices) {
        for (const si of indices) highlightedIndices.add(si);
      }
    }

    for (let i = 0; i < this.shots.length; i++) {
      const shot = this.shots[i];
      const tracer = shot.getTracer();
      tracer.setVisible(true);
      const c = shot.color;
      tracer.recolor(c.r, c.g, c.b);
      tracer.setSelected(configIndex >= 0 && highlightedIndices.has(i));
    }
  }

  // ── Private: Ball testing shots ──────────────────────────────────────────

  private updateBallShots(dt: number): void {
    // Step all individual shots
    for (let i = 0; i < this.shots.length; i++) {
      const shot = this.shots[i];
      if (!shot.isComplete()) {
        shot.physicsUpdate(dt);
      }
    }

    // Check each config: when all SHOTS_PER_CONFIG shots complete, aggregate and report
    for (const [configIdx, shotIndices] of this.ballShotsByConfig) {
      if (this.reportedBallConfigs.has(configIdx)) continue;
      if (this.pendingPatterns.has(`ball:${configIdx}`)) continue;

      const allDone = shotIndices.every(si => this.shots[si].isComplete());
      if (!allDone) continue;

      this.reportedBallConfigs.add(configIdx);
      const entry = this.buildAggregatedBallEntry(configIdx);
      if (entry) {
        this.attachPattern(entry, `ball:${configIdx}`);
        entry.configIndex = configIdx;
        this.ballResults.push(entry);
        this.onShotComplete?.(entry.formatted);
        this.onBallShotResult?.(configIdx, entry);
      }
    }

    // Emit in-flight carry data every ~10 physics frames for real-time leaderboard
    this.inFlightFrameCounter++;
    if (this.onInFlightCarry && this.inFlightFrameCounter % 10 === 0) {
      this.emitInFlightCarry('ball');
    }

    const trancheShots = this.shots.slice(this.ballTrancheShotStart);
    if (trancheShots.length > 0 && trancheShots.every(s => s.isComplete()) && this.reportedBallConfigs.size === this.ballTrancheLaunched) {
      if (this.ballTrancheLaunched < this.ballConfigs.length) {
        for (const shot of trancheShots) {
          shot.disposeHeavy();
        }
        this.launchBallTranche();
      } else {
        this._phase = TestPhase.BallResults;
        this.onPhaseChange?.(this._phase);
        this.onAllBallComplete?.(this.ballResults);
        this.emitLandingPositions('ball');
      }
    }
  }

  private launchBallTranche(): void {
    if (!this.baseline) return;

    const start = this.ballTrancheLaunched;
    const end = Math.min(start + BALL_TRANCHE_SIZE, this.ballConfigs.length);
    this.ballTrancheShotStart = this.shots.length;

    const base = this.baseline;

    // Ball phase: full equipment stack = gear winner + real ball.
    // computeModifiers with ball effects ON is the complete physics for the bag.
    // Prior phases skipped ball effects; now we include them.
    // Defensive fallback: if no gear bag selected (e.g. "Spec Baseline" winner),
    // use a neutral bag so ball phase doesn't crash.
    const gearBag = this.selectedGearBag ?? {
      driver: DRIVERS.find(d => d.id === 'avg') || DRIVERS[0],
      irons: IRON_SETS.find(i => i.id === 'avg') || IRON_SETS[0],
      shaft: SHAFTS.find(s => s.id === 'avg') || SHAFTS[0],
      ball: GOLF_BALLS.find(b => b.id === 'avg') || GOLF_BALLS[0],
    };

    for (let configIdx = start; configIdx < end; configIdx++) {
      const entry = this.ballConfigs[configIdx];
      const shotIndices: number[] = [];

      // Full gear + ball physics — computeModifiers is the complete equipment stack
      const testBag: EquipmentBag = { driver: gearBag.driver, irons: gearBag.irons, shaft: gearBag.shaft, ball: entry.ball };
      const delivery = comparisonDelivery(base, this.eldrickLaunch ?? this.baselineReference);
      const fullMods = computeModifiers(this.clubKey, testBag, base.clubSpeed, delivery.tempo, delivery.transition, false, { attackAngle: base.attackAngle });
      const adjusted = applyModifiers(base, fullMods);

      const baseParams = createSwingParams(
        base.clubSpeed,
        adjusted.ballSpeed,
        adjusted.launchAngle,
        delivery.launchDirection,
        adjusted.spinRate,
        delivery.spinAxis,
        base.attackAngle,
        base.clubPath,
        base.faceAngle,
        delivery.tempo,
        delivery.transition,
      );

      // No variance in ball phase — every ball launches on the exact same line.
      // The background pattern reuses the same full delivery cohort.
      this.queueAdjustedPattern(`ball:${configIdx}`, baseParams);
      for (let s = 0; s < SHOTS_PER_CONFIG; s++) {
        const shot = new ComparisonShot(this.scene, this.shadowGen, entry.color, this.comparisonEnvironment);
        shot.launch(this.representativeParams(baseParams));
        const shotIdx = this.shots.length;
        this.shots.push(shot);
        shotIndices.push(shotIdx);
      }

      this.ballShotsByConfig.set(configIdx, shotIndices);
    }

    this.ballTrancheLaunched = end;
    this.onFlightFramingChanged?.(this.getFlightFramingPoints());
    this.ballTrancheIndex++;
    this.onComparisonProgress?.(this.ballTrancheLaunched, this.ballConfigs.length);
  }

  /** Build an averaged ComparisonEntry for all shots in a ball config. */
  private buildAggregatedBallEntry(configIdx: number): ComparisonEntry | null {
    const shotIndices = this.ballShotsByConfig.get(configIdx);
    if (!shotIndices) return null;

    const ballEntry = this.ballConfigs[configIdx];
    const allFormatted: Record<string, string>[] = [];
    const spinRates: number[] = [];

    for (const si of shotIndices) {
      const data = this.shots[si].getResult();
      if (data) allFormatted.push(data.formatted);
      spinRates.push(this.shots[si].getSpinRate());
    }

    if (allFormatted.length === 0) return null;

    const avgFormatted = computeAverageFormatted(allFormatted, spinRates);
    const spread = this.computeSpread(shotIndices);

    return {
      label: ballEntry.label,
      specs: null,
      color: { r: ballEntry.color.r, g: ballEntry.color.g, b: ballEntry.color.b },
      colorHex: ballEntry.color.hex,
      formatted: avgFormatted,
      specSummary: buildBallSummary(ballEntry.ball),
      spinRate: spinRates.reduce((a, b) => a + b, 0) / spinRates.length,
      spread,
    };
  }

  // ── Private: Cleanup ──────────────────────────────────────────────────────

  private resetPatterns(): void {
    this.patternEpoch++;
    this.patternRunner?.dispose();
    this.patternRunner = null;
    this.patterns.clear();
    this.pendingPatterns.clear();
    this.pairedSwings = [];
    this.patternSource = 'modeled';
    this.review?.dispose();
    this.review = null;
  }

  private queuePattern(key: string, swings: SwingParams[]): void {
    this.patternRunner ??= new PatternRunner();
    if (!this.patternRunner.available || !swings.length) return;
    const epoch = this.patternEpoch;
    this.pendingPatterns.add(key);
    void this.patternRunner.run({ id: key, cohortId: `session-${epoch}`, source: this.patternSource,
      swings, environment: this.comparisonEnvironment }).then(pattern => {
      if (epoch !== this.patternEpoch) return;
      this.pendingPatterns.delete(key);
      if (pattern) this.patterns.set(key, pattern);
      if (key === 'baseline' && this.results[0]) this.attachPattern(this.results[0], key);
    });
  }

  private queueAdjustedPattern(key: string, adjusted: SwingParams): void {
    const reference = this.eldrickLaunch ?? this.baselineReference;
    if (reference) this.queuePattern(key, pairedEquipmentSwings(this.pairedSwings, reference, adjusted));
  }

  private representativeParams(adjusted: SwingParams): SwingParams {
    const reference = this.eldrickLaunch ?? this.baselineReference;
    return reference && this.pairedSwings.length
      ? pairedEquipmentSwings([this.pairedSwings[0]], reference, adjusted)[0] : adjusted;
  }

  private attachPattern(entry: ComparisonEntry, key: string): void {
    const pattern = this.patterns.get(key);
    if (!pattern) return;
    entry.pattern = pattern;
    const median = (read: (s: ShotPattern['shots'][number]) => number) => quantile(pattern.shots.map(read), .5).toFixed(1);
    entry.formatted = { ...entry.formatted, carry: pattern.typicalCarry.toFixed(1), total: pattern.typicalTotal.toFixed(1),
      offline: pattern.typicalOffline.toFixed(1), offlineDir: '',
      height: median(s => s.result.maxHeight * M_TO_YARDS), landing: median(s => s.result.landingAngle),
      time: median(s => s.result.flightTime),
    };
    entry.spinRate = quantile(pattern.shots.map(s => s.spinRate), .5);
    // Legacy table spread is a full width, not ± error/confidence.
    entry.spread = { carrySpread: pattern.carryWidth, offlineSpread: pattern.landingWidth };
  }

  showComparison(current: ComparisonEntry, selected: ComparisonEntry): boolean {
    if (!current.pattern || !selected.pattern) {
      this.review?.dispose(); this.review = null;
      return false;
    }
    this.review ??= new ComparisonReview(this.scene);
    this.review.show(current.pattern, selected.pattern, selected.colorHex);
    this.onFlightFramingChanged?.(this.getFlightFramingPoints());
    return true;
  }

  focusComparison(which: 'current' | 'selected'): void { this.review?.focus(which); }
  replayComparison(): void { this.review?.replay(); }
  setLandingPatterns(visible: boolean): void { this.review?.setPatternsVisible(visible); }

  private disposeShots(): void {
    this.review?.dispose();
    this.review = null;
    for (const shot of this.shots) {
      shot.dispose();
    }
    this.shots = [];
  }

  private disposeBaselineShots(): void {
    for (const shot of this.baselineShots) {
      shot.dispose();
    }
    this.baselineShots = [];
  }
}
